/* ===================================================================
   PostActivationCardAsk -- the REVERSE-TRIAL card ask (Phase 5, spec §7).

   Steve's LOCKED decision (2026-07-07): do NOT deploy the front-door card
   wall. Let owners in card-free, get them to the activation event, THEN ask
   for the card -- the "you got value, now start your plan" moment. This is that
   moment. It is NOT a wall: it is dismissible and never blocks the product.

   WHEN IT SHOWS (all must hold):
     1. Entry model is 'reverse_trial' (the default). In the 'front_door_wall'
        arm the card was already collected at the door, so this stays silent.
     2. NOT a whitelisted operator/demo account (they never see a billing ask).
     3. No active PAID subscription -- has_active_subscription === false. TRUE
        already means a trial-with-card or an active plan (card on file), so we
        never re-ask them. If the plan call errored we stay silent (fail-open,
        matching the gate -- never nag on a transient billing-service blip).
     4. The ACTIVATION EVENT is reached: google_connection_status === 'connected'
        AND requests_sent >= 1 (spec §1 minimum-viable activation). Both are read
        from useSetupProgress (the same derived, real-data signals the checklist
        uses -- never a "clicked Next" flag).

   ESCALATION (coordinates with TrialBar's 72h urgent window):
     - CALM: a dismissible bottom toast -- "you're live, start your plan".
     - ESCALATED (trial in its final 72h or already expired): a centered,
       still-dismissible modal that reasserts the ask before value lapses. The
       two dismissals are tracked separately so closing the calm toast does not
       suppress the expiry escalation.

   The CTA reuses the existing plan+card screen (/choose-plan -> ChoosePlan ->
   api.startCheckout), which also captures the REQUIRED Terms/TCPA consent. On
   checkout success the account becomes subscribed -> has_active_subscription
   flips true -> this ask stops showing.

   TOKENS ONLY: colors come from tokens.css var(--...); no hardcoded brand hex.
   =================================================================== */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  useEntryModel,
  useSubscriptionAccess,
  usePlan,
  useMe,
  useSetupProgress,
} from '../../hooks';
import '../../styles/overlays.css';

// Mirror TrialBar's escalation threshold so the two surfaces agree on "final
// stretch". (TrialBar keeps its own copy; kept in sync intentionally.)
const URGENT_WINDOW_MS = 72 * 3_600_000;

function sessionFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
function setSessionFlag(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* non-fatal */
  }
}

export function PostActivationCardAsk() {
  const navigate = useNavigate();
  const entryModel = useEntryModel();
  const access = useSubscriptionAccess();
  const plan = usePlan();
  const me = useMe();
  const progress = useSetupProgress();

  const clientId = me.data?.client_id ?? '';
  const calmKey = `rhody_pac_calm_dismissed_${clientId}`;
  const escKey = `rhody_pac_esc_dismissed_${clientId}`;

  // Session-scoped dismissals (re-surface next session, never nag within one).
  const [calmDismissed, setCalmDismissed] = useState(() => sessionFlag(calmKey));
  const [escDismissed, setEscDismissed] = useState(() => sessionFlag(escKey));

  // ---- Gating (never render for the wrong audience / wrong arm) ----
  // Only the reverse-trial arm. The wall arm collected the card at the door.
  if (entryModel !== 'reverse_trial') return null;
  // Operators / demo never see a billing ask.
  if (access.whitelisted) return null;
  // Hold until the underlying signals settle (never flash at a fully-set-up or
  // still-loading account).
  if (progress.loading) return null;
  // Already paying/committed (trial-with-card or active) -> no ask. On a plan
  // error we do not know the status -> stay silent (fail-open, matches gate).
  if (plan.isError) return null;
  if (plan.data?.has_active_subscription === true) return null;

  // ---- Activation event: connected Google + at least one request sent ----
  const gbpDone = progress.steps.find((s) => s.id === 'gbp')?.done === true;
  const askSent = progress.steps.find((s) => s.id === 'requests')?.done === true;
  if (!gbpDone || !askSent) return null;

  // ---- Escalate as the trial nears expiry (coordinate with TrialBar) ----
  const endMs = me.data?.trial_ends_at ? Date.parse(me.data.trial_ends_at) : NaN;
  const hasDeadline = Number.isFinite(endMs);
  const now = Date.now();
  const remMs = hasDeadline ? endMs - now : NaN;
  const expired = hasDeadline && remMs <= 0;
  const escalate = hasDeadline && remMs <= URGENT_WINDOW_MS; // final 72h or past

  function goToPlan() {
    navigate('/choose-plan');
  }

  function dismissCalm() {
    setSessionFlag(calmKey);
    setCalmDismissed(true);
  }
  function dismissEsc() {
    setSessionFlag(escKey);
    setEscDismissed(true);
  }

  // ===== ESCALATED: centered (still dismissible) modal near/after expiry =====
  if (escalate) {
    if (escDismissed) return null;
    const daysLeft = hasDeadline ? Math.max(0, Math.ceil(remMs / 86_400_000)) : 0;
    const modal = (
      <div
        className="rh-pac"
        role="dialog"
        aria-modal="true"
        aria-label="Start your plan"
      >
        <div className="rh-pac__card">
          <div className="rh-pac__head">
            <div className="rh-pac__glyph" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6}>
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="rh-pac__h">
              {expired ? 'Your free trial has ended' : 'Keep your reviews coming'}
            </h2>
            <p className="rh-pac__p">
              {expired
                ? "You're already collecting reviews. Start your plan to keep your review link, requests, and replies live."
                : `Your free trial ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}. Start your plan now so your review requests never pause.`}
            </p>
          </div>
          <div className="rh-pac__actions">
            <button className="rh-pac__cta" onClick={goToPlan}>
              Choose a plan
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="rh-pac__later" onClick={dismissEsc}>
              Not now
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(modal, document.body);
  }

  // ===== CALM: non-blocking bottom toast (the "you got value" moment) =====
  if (calmDismissed) return null;
  const toast = (
    <div className="rh-pac-toast" role="status" aria-live="polite">
      <div className="rh-pac-toast__card">
        <div className="rh-pac-toast__glyph" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.6 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
          </svg>
        </div>
        <div className="rh-pac-toast__body">
          <div className="rh-pac-toast__title">Your review engine is live</div>
          <div className="rh-pac-toast__sub">
            Your first requests are on their way. Start your plan to keep them coming.
          </div>
        </div>
        <div className="rh-pac-toast__tools">
          <button className="rh-pac-toast__cta" onClick={goToPlan}>
            Start your plan
          </button>
          <button
            className="rh-pac-toast__x"
            aria-label="Dismiss"
            onClick={dismissCalm}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(toast, document.body);
}
